import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Fade from '@mui/material/Fade';
import type React from 'react';
import DialogHeader from './DialogHeader';
import DialogSkeleton from './DialogSkeleton';

interface AkomiDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when the dialog requests to close (backdrop click, escape, close button) */
  onClose: () => void;
  /** Optional dialog title rendered via DialogHeader */
  title?: string;
  /** MUI Dialog maxWidth breakpoint */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  /** Whether the dialog stretches to maxWidth (default true) */
  fullWidth?: boolean;
  /** When true, shows skeleton placeholders instead of children */
  loading?: boolean;
  /** Custom skeleton layout to show when loading=true (falls back to DialogSkeleton) */
  skeleton?: React.ReactNode;
  /** Main dialog content (optional when loading=true) */
  children?: React.ReactNode;
  /** Action buttons rendered at the bottom of the dialog */
  actions?: React.ReactNode;
  /** Number of skeleton rows to show when loading (default 3) */
  skeletonRows?: number;
  /** Controls overall layout variant */
  variant?: 'default' | 'confirm' | 'form';
}

/**
 * AkomiDialog — unified modal shell.
 *
 * Uses MUI Dialog for portal/focus-trap/backdrop/a11y,
 * but replaces DialogTitle/DialogContent/DialogActions with plain Box
 * to match the hand-crafted mockup styling exactly.
 */
const AkomiDialog: React.FC<AkomiDialogProps> = ({
  open,
  onClose,
  title,
  maxWidth = 'sm',
  fullWidth = true,
  loading = false,
  skeleton,
  children,
  actions,
  skeletonRows = 3,
  variant = 'default',
}) => {
  const skeletonVariant = variant === 'form' ? 'form' : 'text';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          bgcolor: (theme) => theme.palette.background.paper,
          borderRadius: '8px',
          overflow: 'hidden',
          // Mockup shadow — matches .modal box-shadow exactly
          boxShadow:
            '0 11px 15px -7px rgba(0,0,0,0.2), 0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* Header — stable layout anchor, always visible */}
      {title && <DialogHeader header={title} onClose={loading ? undefined : onClose} />}

      {/* Content area — padding: 0 24px 16px (matches .modal-content) */}
      <Box
        sx={{
          px: 3, // 24px
          pb: '20px',
          pt: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: loading ? 120 : undefined,
        }}
      >
        {loading ? (
          (skeleton ?? <DialogSkeleton rows={skeletonRows} hasTitle={!title} variant={skeletonVariant} />)
        ) : (
          <Fade in timeout={200}>
            <div>{children}</div>
          </Fade>
        )}
      </Box>

      {/* Actions — inert wrapper during loading blocks double-submits */}
      {loading ? <Box sx={{ pointerEvents: 'none' }}>{actions}</Box> : actions}
    </Dialog>
  );
};

export type { AkomiDialogProps };
export { AkomiDialog };
export default AkomiDialog;
